import { Grid, Button, TextField } from "@material-ui/core";
import {
  IconEdit,
  IconEraser,
  IconFilter,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import CustomDialog from "../../../components/common/dialogs/CustomDialog";
import AddNewReglaForm, { LoadingInput } from "../forms/AddNewReglaForm";
import {
  useDisableRuleMutation,
  useGetCommoditiesQuery,
  useGetContainersQuery,
  useGetDestinationsQuery,
  useGetOriginsQuery,
  useGetProvidersQuery,
  useLazyGetRulesQuery,
} from "../../../services/costs/costsService";
import { CustomIconButton } from "../../../components/common/icon-buttons/CustomIconButton";
import { Rule } from "../../../interfaces/costs/costs.rules.interface";
import { headers } from "../constants/headers";
import { Autocomplete } from "@material-ui/lab";
import CustomTable from "../../../components/common/table/CustomTable";
import { useFormik } from "formik";

const ReglasList = () => {
  const [keyAutocomplete, setKeyAutocomplete] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [showNewForm, setShowNewForm] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number>();
  const [ruleToEdit, setRuleToEdit] = useState<Rule>();

  const openNewModal = () => {
    setShowNewForm(true);
  };

  const closeNewModal = () => {
    setShowNewForm(false);
  };

  const handleSetIdToDelete = (id: number) => {
    setIdToDelete(id);
  };

  const handleUnsetIdToDelete = () => {
    setIdToDelete(undefined);
    reset();
  };

  const handleSetRuleToEdit = (rule: Rule) => {
    setRuleToEdit(rule);
  };

  const handleUnsetRuleToDelete = () => {
    setRuleToEdit(undefined);
  };

  const { values, setFieldValue, handleSubmit, resetForm } = useFormik({
    initialValues: {
      origen: undefined,
      destino: undefined,
      naviero: undefined,
      commodity: undefined,
      contenedor: undefined,
      contract: "",
    },
    onSubmit() {
      fetchRules();
    },
  });

  // Options Autocomplete
  const { isLoading: isLoadingProviders, data: dataProviders } =
    useGetProvidersQuery();

  const { data: dataOrigins, isLoading: isLoadingOrigins } =
    useGetOriginsQuery();

  const { data: dataDestiantions, isLoading: isLoadingDestinations } =
    useGetDestinationsQuery();

  const { data: dataCommodities, isLoading: isLoadingCommodities } =
    useGetCommoditiesQuery();

  const { isLoading: isLoadingContainers, data: dataContainers } =
    useGetContainersQuery();

  const providersOptions =
    dataProviders?.datos
      .filter((or) => or.nombre !== "UNDEFINED")
      .map((or) => ({
        label: or.nombre,
        value: or.nombre,
      })) || [];

  const originsOptions =
    dataOrigins?.datos
      .filter((or) => or.nombre !== "UNDEFINED")
      .map((or) => ({
        label: or.nombre,
        value: or.nombre,
      })) || [];

  const destinationsOptions =
    dataDestiantions?.datos
      .filter((des) => des.nombre !== "UNDEFINED")
      .map((des) => ({
        label: des.nombre,
        value: des.nombre,
      })) || [];

  const commoditiesOptions =
    dataCommodities?.datos.map((comm) => ({
      label: comm.nombre,
      value: comm.nombre,
    })) || [];

  const containersOptions =
    dataContainers?.datos.map((ct) => ({
      label: ct.nombre,
      value: ct.nombre,
    })) || [];

  const [
    getRules,
    { data: dataRules, isLoading: isLoadingRules, isFetching: isFetchingRules, isError: isErrorRules },
  ] = useLazyGetRulesQuery();

  const fetchRules = useCallback(() => {
    getRules({
      page,
      size,
      commodity: values.commodity,
      contenedor: values.contenedor,
      destino: values.destino,
      naviero: values.naviero,
      origen: values.origen,
      contract: values.contract.length > 0 ? values.contract.trim() : undefined,
    });
  }, [values, getRules, page, size]);

  const [
    disableRule,
    {
      isLoading: isLoadingDisable,
      isSuccess: isSuccessDisable,
      isError: isErrorDisable,
      reset,
    },
  ] = useDisableRuleMutation();

  const rows =
    dataRules?.data.map((rl, index) => ({
      number: index + page * size + 1,
      naviero: rl.naviero,
      origen: rl.origen,
      destino: rl.destino,
      commodity: rl.commodity,
      contenedor: rl.contenedor,
      contract: rl.contract_number,
      notes: (
        <textarea
          value={rl.notes}
          rows={3}
          readOnly={true}
          style={{ width: 160 }}
        />
      ),
      f_creacion: rl.fecha_creacion.substring(0, 10).replaceAll("-", "/"),
      options: (
        <div style={{ display: "flex", flex: 1, flexDirection: "row" }}>
          <CustomIconButton
            toolTipText="Editar"
            icon={<IconEdit color="orange" />}
            onClick={() => handleSetRuleToEdit(rl)}
          />
          <CustomIconButton
            toolTipText="Eliminar"
            icon={<IconTrash color="red" />}
            onClick={() => handleSetIdToDelete(Number(rl.id_regla))}
          />
        </div>
      ),
    })) || [];

  const handleDisable = () => {
    disableRule({
      id_regla: idToDelete!,
    });
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size]);

  useEffect(() => {
    if (isSuccessDisable) {
      fetchRules();
    }
  }, [isSuccessDisable, fetchRules]);

  const handleClearFilters = () => {
    setKeyAutocomplete((key) => key + 1);
    resetForm();
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          {isLoadingProviders ? (
            <LoadingInput />
          ) : (
            <Autocomplete
              key={keyAutocomplete}
              onChange={(e, val) => {
                setFieldValue("naviero", val?.value);
                setPage(0);
              }}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              options={providersOptions}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Naviero" />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {isLoadingOrigins ? (
            <LoadingInput />
          ) : (
            <Autocomplete
              key={keyAutocomplete}
              onChange={(e, val) => {
                setFieldValue("origen", val?.value);
                setPage(0);
              }}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              options={originsOptions}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Origen" />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {isLoadingDestinations ? (
            <LoadingInput />
          ) : (
            <Autocomplete
              key={keyAutocomplete}
              onChange={(e, val) => {
                setFieldValue("destino", val?.value);
                setPage(0);
              }}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              options={destinationsOptions}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Destino" />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {isLoadingCommodities ? (
            <LoadingInput />
          ) : (
            <Autocomplete
              key={keyAutocomplete}
              onChange={(e, val) => {
                setFieldValue("commodity", val?.value);
                setPage(0);
              }}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              options={commoditiesOptions}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Commodity" />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {isLoadingContainers ? (
            <LoadingInput />
          ) : (
            <Autocomplete
              key={keyAutocomplete}
              onChange={(e, val) => {
                setFieldValue("contenedor", val?.value);
                setPage(0);
              }}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              options={containersOptions}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Contenedor" />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="Contract Number"
            onChange={(e) => {
              setFieldValue("contract", String(e.target.value).toUpperCase());
              setPage(0);
            }}
            value={values.contract}
          />
        </Grid>
        <Grid
          item
          container
          xs={12}
          md={12}
          style={{ justifyContent: "flex-end", marginBottom: 10 }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<IconFilter />}
            onClick={() => handleSubmit()}
          >
            Filtrar
          </Button>
          &nbsp;&nbsp;
          <Button
            variant="contained"
            color="secondary"
            startIcon={<IconEraser />}
            onClick={handleClearFilters}
          >
            Limpiar Filtros
          </Button>
        </Grid>
      </Grid>
      <Grid item container xs={12} justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<IconPlus />}
          onClick={openNewModal}
        >
          Crear Regla
        </Button>
      </Grid>
      <Grid item xs={12}>
        <CustomTable
          dense={true}
          isLoading={isLoadingRules || isFetchingRules}
          isError={isErrorRules}
          headers={headers}
          rows={rows}
          totalRecords={Number(dataRules?.total_rows) || 0}
          onChangePage={(val) => {
            setPage(val);
          }}
          onChangeRowsPerPage={(val) => {
            setSize(val);
          }}
          page={page}
          rowsPerPage={size}
        />
      </Grid>
      {showNewForm && (
        <CustomDialog
          handleClose={closeNewModal}
          isOpen={showNewForm}
          title="Crear Regla"
          hideActionButtons
          maxWidth="md"
        >
          <AddNewReglaForm refecth={fetchRules} />
        </CustomDialog>
      )}
      {Boolean(ruleToEdit) && (
        <CustomDialog
          handleClose={handleUnsetRuleToDelete}
          isOpen={Boolean(ruleToEdit)}
          title="Editar Regla"
          hideActionButtons
          maxWidth="md"
        >
          <AddNewReglaForm refecth={fetchRules} ruleToEdit={ruleToEdit} />
        </CustomDialog>
      )}
      {Boolean(idToDelete) && (
        <CustomDialog
          title="Eliminar Regla"
          subtitle="¿Esta séguro de eliminar esta regla?"
          handleClose={handleUnsetIdToDelete}
          isOpen={Boolean(idToDelete)}
          handleSubmit={handleDisable}
          isLoading={isLoadingDisable}
          isError={isErrorDisable}
          isSuccess={isSuccessDisable}
          errorMessage="Ocurrio un error. Intente luego"
          successMessage="Regla eliminada correctamente"
          textButton="Eliminar"
          maxWidth="xs"
        />
      )}
    </>
  );
};

export default ReglasList;
